import { useCallback, useEffect, useMemo, useRef } from "react"
import { useNavigate } from "@tanstack/react-router"
import { zodResolver } from "@hookform/resolvers/zod"
import { useFieldArray, useForm } from "react-hook-form"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { FilePondDocumentUpload } from "@/components/file-pond-upload"
import {
  useIsSavingMembershipApplicationDraft,
  useMembershipApplicationDraft,
  useSaveMembershipApplicationDraft,
  type EducationHistoryDraftEntry,
} from "../api/useMembershipApplicationDraft"
import { membershipApplicationSteps } from "../constants"
import { useApplicationSectionConfiguration } from "../hooks/useApplicationConfiguration"
import { RequirementSummaryList } from "./requirement-summary-list"

const educationEntrySchema = z
  .object({
    id: z.string().optional(),
    institution: z.string().trim().min(2, "Enter the institution name"),
    qualification: z.string().trim().min(2, "Enter the qualification obtained"),
    awardingBody: z.string().trim().min(2, "Enter the awarding body"),
    graduationYear: z.string().optional(),
    fieldOfStudy: z.string().trim().max(160, "Keep the field of study concise").optional(),
    attachmentName: z.string().optional(), // Keep for backward compatibility
    fileId: z.string().optional(),
    fileUrl: z.string().optional(),
    originalFilename: z.string().optional(),
    honors: z.string().trim().max(260, "Keep honours under 260 characters").optional(),
  })
  .superRefine((data, ctx) => {
    if (data.graduationYear && data.graduationYear.length > 0) {
      if (!/^\d{4}$/.test(data.graduationYear)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Provide a four-digit year",
          path: ["graduationYear"],
        })
      }
    }
  })

const educationDraftSchema = z.object({
  entries: z.array(educationEntrySchema),
})

const educationFormSchema = educationDraftSchema.refine((data) => data.entries.length > 0, {
  message: "Add at least one qualification",
  path: ["entries"],
})

type EducationDraftValues = z.infer<typeof educationDraftSchema>
export type EducationFormValues = z.infer<typeof educationDraftSchema>

function cryptoId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
  } catch (_error) {
    // ignore
  }
  return Math.random().toString(36).slice(2, 12)
}

function mapDraftToFormValues(
  entries: EducationHistoryDraftEntry[],
  options?: { ensureEntry?: boolean },
): EducationDraftValues {
  const { ensureEntry = true } = options ?? {}
  if (!entries || entries.length === 0) {
    return { entries: ensureEntry ? [createEmptyEducationEntry()] : [] }
  }

  return {
    entries: entries.map((entry) => ({
      id: entry.id,
      institution: entry.institution ?? "",
      qualification: entry.qualification ?? "",
      awardingBody: entry.awardingBody ?? "",
      graduationYear: entry.graduationYear ?? "",
      fieldOfStudy: entry.fieldOfStudy ?? "",
      attachmentName: entry.attachmentName ?? "",
      honors: entry.honors ?? "",
    })),
  }
}

function mapFormEntriesToDraft(entries: EducationDraftValues["entries"]): EducationHistoryDraftEntry[] {
  return entries.map((entry) => ({
    id: entry.id ?? cryptoId(),
    institution: entry.institution.trim(),
    qualification: entry.qualification.trim(),
    awardingBody: entry.awardingBody.trim(),
    graduationYear: entry.graduationYear?.trim() || undefined,
    fieldOfStudy: entry.fieldOfStudy?.trim() || undefined,
    attachmentName: entry.originalFilename?.trim() || entry.attachmentName?.trim() || undefined,
    honors: entry.honors?.trim() || undefined,
  }))
}

function createEmptyEducationEntry() {
  return {
    id: cryptoId(),
    institution: "",
    qualification: "",
    awardingBody: "",
    graduationYear: "",
    fieldOfStudy: "",
    attachmentName: "",
    honors: "",
  }
}

export function EducationStep() {
  const navigate = useNavigate()
  const { data: draft } = useMembershipApplicationDraft()
  const saveDraftMutation = useSaveMembershipApplicationDraft()
  const isSavingDraft = useIsSavingMembershipApplicationDraft()

  const categoryId = draft?.category?.categoryId ?? null
  const {
    section,
    isVisible,
    isRequired,
    nextStep,
    categoriesQuery,
    category,
  } = useApplicationSectionConfiguration("education", categoryId)

  useEffect(() => {
    if (categoriesQuery.isLoading) {
      return
    }
    if (!isVisible) {
      const fallbackPath = nextStep?.path ?? membershipApplicationSteps[membershipApplicationSteps.length - 1]?.path
      if (fallbackPath) {
        navigate({ to: fallbackPath, replace: true })
      }
    }
  }, [categoriesQuery.isLoading, isVisible, nextStep, navigate])

  if (!isVisible && !categoriesQuery.isLoading) {
    return null
  }

  const formDefaultValues = useMemo(
    () =>
      mapDraftToFormValues(draft?.educationHistory ?? [], {
        ensureEntry: isRequired,
      }),
    [draft?.educationHistory, isRequired],
  )

  const schema = isRequired ? educationFormSchema : educationDraftSchema
  const resolver = useMemo(() => zodResolver(schema), [schema])

  const form = useForm<EducationFormValues>({
    resolver,
    defaultValues: formDefaultValues,
    mode: "onChange",
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "entries",
  })

  useEffect(() => {
    form.reset(formDefaultValues)
  }, [formDefaultValues, form])

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skipNextAutosaveRef = useRef(true)

  useEffect(() => {
    skipNextAutosaveRef.current = true
  }, [formDefaultValues])

  useEffect(() => {
    const subscription = form.watch((values, info) => {
      if (skipNextAutosaveRef.current) {
        skipNextAutosaveRef.current = false
        return
      }

      if (info?.type !== "change" && info?.type !== "blur") {
        return
      }

      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }

      autosaveTimerRef.current = setTimeout(() => {
        const parsed = educationDraftSchema.safeParse(values)
        if (!parsed.success) {
          return
        }

        const payload = mapFormEntriesToDraft(parsed.data.entries)
        saveDraftMutation.mutate({ educationHistory: payload })
      }, 800)
    })

    return () => {
      subscription.unsubscribe()
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
    }
  }, [form, saveDraftMutation])

  const nextStepPath = nextStep?.path

  const handleSubmit = useCallback(
    async (values: EducationFormValues) => {
      const payload = mapFormEntriesToDraft(values.entries)
      await saveDraftMutation.mutateAsync({ educationHistory: payload })

      if (nextStepPath) {
        navigate({ to: nextStepPath })
      }
    },
    [navigate, nextStepPath, saveDraftMutation],
  )

  const isBusy = isSavingDraft || saveDraftMutation.isPending

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold">
              Academic qualifications
              {!isRequired ? (
                <span className="ml-2 text-sm font-medium text-muted-foreground">Optional</span>
              ) : null}
            </h2>
            <p className="text-sm text-muted-foreground">
              {section?.guidance ?? "List your qualifications so we can verify category eligibility."}
            </p>
            {!isRequired ? (
              <p className="text-xs text-muted-foreground">
                {category?.categoryName
                  ? `${category.categoryName} applicants may skip this section if it does not apply.`
                  : "You may skip this section if it does not apply."}
              </p>
            ) : null}
          </div>
          <Button type="button" variant="outline" onClick={() => append(createEmptyEducationEntry())}>
            Add qualification
          </Button>
        </div>

        <div className="space-y-4">
          {section?.requirementSummaries && section.requirementSummaries.length > 0 ? (
            <RequirementSummaryList
              title="What to include"
              summaries={section.requirementSummaries}
            />
          ) : null}

          {fields.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No qualifications recorded yet. Add your first qualification to continue.
              </CardContent>
            </Card>
          ) : null}

          {fields.map((field, index) => (
            <Card key={field.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-base">Qualification {index + 1}</CardTitle>
                  <CardDescription>Include the awarding body and year completed.</CardDescription>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(index)}
                  className="text-destructive"
                >
                  Remove
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name={`entries.${index}.institution`}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel required>Institution</FormLabel>
                        <FormControl>
                          <Input placeholder="Name of institution" data-invalid={fieldState.invalid} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`entries.${index}.qualification`}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel required>Qualification</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. BSc Accounting" data-invalid={fieldState.invalid} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name={`entries.${index}.awardingBody`}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel required>Awarding body</FormLabel>
                        <FormControl>
                          <Input placeholder="Awarding authority" data-invalid={fieldState.invalid} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`entries.${index}.graduationYear`}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Graduation year (optional)</FormLabel>
                        <FormControl>
                          <Input type="number" min={1900} max={2100} data-invalid={fieldState.invalid} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name={`entries.${index}.fieldOfStudy`}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Field of study (optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="Major or focus" data-invalid={fieldState.invalid} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormItem>
                    <FormLabel>Upload transcript/certificate (optional)</FormLabel>
                    <FormControl>
                      <FilePondDocumentUpload
                        maxFileSize="20MB"
                        isAuthenticated={false}
                        usernameOrEmail={draft?.profile?.email ?? undefined}
                        onUploadComplete={(fileId, fileUrl, filename) => {
                          form.setValue(`entries.${index}.fileId`, fileId)
                          form.setValue(`entries.${index}.fileUrl`, fileUrl)
                          form.setValue(`entries.${index}.originalFilename`, filename)
                        }}
                        onUploadError={(error) => {
                          form.setError(`entries.${index}.fileId`, {
                            type: "manual",
                            message: error,
                          })
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>

                <FormField
                  control={form.control}
                  name={`entries.${index}.honors`}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Honours and notes (optional)</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Mention honours or key remarks" data-invalid={fieldState.invalid} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          ))}
        </div>

        {form.formState.errors.entries ? (
          <p className="text-sm text-destructive">{form.formState.errors.entries.message}</p>
        ) : null}

        <CardFooter className="flex justify-end">
          <Button type="submit" disabled={isBusy}>
            {isBusy ? "Saving..." : "Continue"}
          </Button>
        </CardFooter>
      </form>
    </Form>
  )
}
